import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { DollarSign, TrendingUp, Target, Users, PieChart as PieChartIcon, ShoppingCart, Heart, Smartphone, Home, Store, Settings, ChevronDown } from 'lucide-react';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28DFF', '#FF6B6B'];

const niches = [
  'E-commerce',
  'Saúde e Bem-estar',
  'Tecnologia e SaaS',
  'Imobiliário',
  'Vendas/Varejo',
  'Serviços Profissionais'
];

const MetricsDisplay: React.FC = () => {
  const [selectedNiche, setSelectedNiche] = useState<string>('E-commerce');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const nichesData = {
    'E-commerce': {
      icon: <ShoppingCart className="h-8 w-8 text-green-500" />,
      cpc: 1.80,
      ctr: 4.5,
      conversions: 28,
      reach: '22.5k',
      roi: 3.8,
      budget: 12000,
      performanceData: [
        { name: 'Q1', cpc: 1.95, ctr: 4.2, conversions: 24 },
        { name: 'Q2', cpc: 1.85, ctr: 4.4, conversions: 26 },
        { name: 'Q3', cpc: 1.78, ctr: 4.6, conversions: 29 },
        { name: 'Q4', cpc: 1.70, ctr: 4.8, conversions: 32 },
      ],
      platformDistribution: [
        { name: 'Google Ads', value: 55 },
        { name: 'Meta Ads', value: 35 },
        { name: 'TikTok Ads', value: 10 },
      ],
      recommendations: {
        google: [
          'Performance Max: R$ 65/dia (priorizar feed de produtos)',
          'Pesquisa com palavras-chave comerciais: R$ 45/dia',
          'YouTube Shopping: R$ 30/dia'
        ],
        meta: [
          'Dynamic Ads: R$ 60/dia (ênfase em catálogo)',
          'Campanhas de conversão: R$ 40/dia',
          'Stories Ads: R$ 25/dia'
        ],
        general: [
          'Investir em automação de bids',
          'Testar Google Discovery Ads',
          'Otimizar para mobile (75% do tráfego)'
        ]
      }
    },
    'Saúde e Bem-estar': {
      icon: <Heart className="h-8 w-8 text-red-500" />,
      cpc: 2.40,
      ctr: 3.2,
      conversions: 18,
      reach: '15.8k',
      roi: 2.5,
      budget: 8000,
      performanceData: [
        { name: 'Q1', cpc: 2.60, ctr: 3.0, conversions: 16 },
        { name: 'Q2', cpc: 2.45, ctr: 3.1, conversions: 17 },
        { name: 'Q3', cpc: 2.35, ctr: 3.3, conversions: 19 },
        { name: 'Q4', cpc: 2.25, ctr: 3.5, conversions: 20 },
      ],
      platformDistribution: [
        { name: 'Google Ads', value: 50 },
        { name: 'Meta Ads', value: 40 },
        { name: 'LinkedIn Ads', value: 10 },
      ],
      recommendations: {
        google: [
          'Campanhas de display em sites de saúde: R$ 40/dia',
          'YouTube para vídeos educativos: R$ 35/dia',
          'Pesquisa com palavras-chave de problemas/soluções: R$ 45/dia'
        ],
        meta: [
          'Lead Ads para captação: R$ 50/dia',
          'Carrossel para depoimentos: R$ 30/dia',
          'Audience Network para apps de saúde: R$ 20/dia'
        ],
        general: [
          'Focar em conteúdo educativo',
          'Utilizar remarketing de 30 dias',
          'Testar Google Performance Max para assinaturas'
        ]
      }
    },
    'Tecnologia e SaaS': {
      icon: <Smartphone className="h-8 w-8 text-blue-500" />,
      cpc: 3.10,
      ctr: 2.8,
      conversions: 12,
      reach: '12.3k',
      roi: 4.2,
      budget: 15000,
      performanceData: [
        { name: 'Q1', cpc: 3.30, ctr: 2.6, conversions: 10 },
        { name: 'Q2', cpc: 3.20, ctr: 2.7, conversions: 11 },
        { name: 'Q3', cpc: 3.05, ctr: 2.9, conversions: 13 },
        { name: 'Q4', cpc: 2.95, ctr: 3.0, conversions: 14 },
      ],
      platformDistribution: [
        { name: 'Google Ads', value: 60 },
        { name: 'Meta Ads', value: 25 },
        { name: 'LinkedIn Ads', value: 15 },
      ],
      recommendations: {
        google: [
          'Campanhas de pesquisa B2B: R$ 70/dia',
          'Gmail Ads para whitepapers: R$ 30/dia',
          'Display para públicos específicos: R$ 50/dia'
        ],
        meta: [
          'Conversões para trials: R$ 45/dia',
          'Lead generation: R$ 35/dia',
          'Video views para webinars: R$ 30/dia'
        ],
        general: [
          'Focar em conversões qualificadas (MQLs)',
          'Testar LinkedIn Sponsored Content',
          'Otimizar para desktop (60% do tráfego)'
        ]
      }
    },
    'Imobiliário': {
      icon: <Home className="h-8 w-8 text-purple-500" />,
      cpc: 2.80,
      ctr: 3.5,
      conversions: 15,
      reach: '18.7k',
      roi: 3.0,
      budget: 10000,
      performanceData: [
        { name: 'Q1', cpc: 3.00, ctr: 3.3, conversions: 13 },
        { name: 'Q2', cpc: 2.90, ctr: 3.4, conversions: 14 },
        { name: 'Q3', cpc: 2.75, ctr: 3.6, conversions: 16 },
        { name: 'Q4', cpc: 2.65, ctr: 3.7, conversions: 17 },
      ],
      platformDistribution: [
        { name: 'Google Ads', value: 65 },
        { name: 'Meta Ads', value: 30 },
        { name: 'YouTube Ads', value: 5 },
      ],
      recommendations: {
        google: [
          'Local Services Ads: R$ 60/dia',
          'Pesquisa para termos de bairros: R$ 50/dia',
          'Display em portais imobiliários: R$ 40/dia'
        ],
        meta: [
          'Carrossel para imóveis: R$ 55/dia',
          'Lead Ads para captação: R$ 35/dia',
          'Stories para tours virtuais: R$ 30/dia'
        ],
        general: [
          'Priorizar imagens de alta qualidade',
          'Remarketing de 90 dias (ciclo longo)',
          'Testar Google Local Campaigns'
        ]
      }
    },
    'Vendas/Varejo': {
      icon: <Store className="h-8 w-8 text-orange-500" />,
      cpc: 2.20,
      ctr: 4.0,
      conversions: 22,
      reach: '20.1k',
      roi: 3.5,
      budget: 11000,
      performanceData: [
        { name: 'Q1', cpc: 2.40, ctr: 3.8, conversions: 20 },
        { name: 'Q2', cpc: 2.30, ctr: 3.9, conversions: 21 },
        { name: 'Q3', cpc: 2.15, ctr: 4.1, conversions: 23 },
        { name: 'Q4', cpc: 2.05, ctr: 4.3, conversions: 25 },
      ],
      platformDistribution: [
        { name: 'Google Ads', value: 50 },
        { name: 'Meta Ads', value: 45 },
        { name: 'Google Local', value: 5 },
      ],
      recommendations: {
        google: [
          'Smart Shopping: R$ 60/dia',
          'Campanhas de pesquisa local: R$ 40/dia',
          'Display para promoções: R$ 35/dia'
        ],
        meta: [
          'Conversões para loja física: R$ 55/dia',
          'Stories para promoções relâmpago: R$ 30/dia',
          'Audience local (5km): R$ 25/dia'
        ],
        general: [
          'Integrar com Google My Business',
          'Ofertas exclusivas para mobile',
          'Remarketing de 7 dias (ciclo curto)'
        ]
      }
    },
    'Serviços Profissionais': {
      icon: <Settings className="h-8 w-8 text-teal-500" />,
      cpc: 2.60,
      ctr: 3.3,
      conversions: 16,
      reach: '14.9k',
      roi: 3.2,
      budget: 9000,
      performanceData: [
        { name: 'Q1', cpc: 2.80, ctr: 3.1, conversions: 14 },
        { name: 'Q2', cpc: 2.70, ctr: 3.2, conversions: 15 },
        { name: 'Q3', cpc: 2.55, ctr: 3.4, conversions: 17 },
        { name: 'Q4', cpc: 2.45, ctr: 3.5, conversions: 18 },
      ],
      platformDistribution: [
        { name: 'Google Ads', value: 70 },
        { name: 'Meta Ads', value: 25 },
        { name: 'LinkedIn Ads', value: 5 },
      ],
      recommendations: {
        google: [
          'Pesquisa para serviços emergenciais: R$ 50/dia',
          'Local Services Ads: R$ 40/dia',
          'Display em sites especializados: R$ 30/dia'
        ],
        meta: [
          'Lead Ads para orçamentos: R$ 45/dia',
          'Carrossel de depoimentos: R$ 25/dia',
          'Vídeos curtos explicativos: R$ 20/dia'
        ],
        general: [
          'Destaque certificações e avaliações',
          'CTA claro para contato imediato',
          'Google Guaranteed para serviços qualificados'
        ]
      }
    }
  };

  const currentNiche = nichesData[selectedNiche as keyof typeof nichesData];

  return (
    <div className="space-y-6">
      <div className="relative">
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center justify-between w-full md:w-64 bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center">
            {currentNiche.icon}
            <span className="ml-3 text-lg font-medium text-gray-900 dark:text-white">{selectedNiche}</span>
          </div>
          <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${isDropdownOpen ? 'transform rotate-180' : ''}`} />
        </button>
        
        {isDropdownOpen && (
          <div className="absolute z-10 mt-1 w-full md:w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
            {niches.map((niche) => (
              <button
                key={niche}
                onClick={() => {
                  setSelectedNiche(niche);
                  setIsDropdownOpen(false);
                }}
                className={`w-full text-left px-4 py-3 flex items-center hover:bg-gray-100 dark:hover:bg-gray-700 ${selectedNiche === niche ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
              >
                {nichesData[niche as keyof typeof nichesData].icon}
                <span className="ml-3 text-gray-900 dark:text-white">{niche}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">CPC Médio</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">R$ {currentNiche.cpc.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">CTR</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{currentNiche.ctr}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Target className="h-8 w-8 text-orange-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">ROI Esperado</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{currentNiche.roi}x</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-red-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Alcance Mensal</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{currentNiche.reach}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <PieChartIcon className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Orçamento Mensal</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">R$ {currentNiche.budget.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Performance Trimestral (2025)</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={currentNiche.performanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="ctr" stroke="#8884d8" name="CTR (%)" strokeWidth={2} />
                <Line type="monotone" dataKey="conversions" stroke="#82ca9d" name="Conversões" strokeWidth={2} />
                <Line type="monotone" dataKey="cpc" stroke="#ff7300" name="CPC (R$)" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Distribuição por Plataforma</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={currentNiche.platformDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {currentNiche.platformDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <img src="https://www.google.com/images/branding/product/1x/ads_48dp.png" alt="Google Ads" className="h-6 w-6 mr-2" />
            Google Ads - Recomendações
          </h3>
          <ul className="space-y-3 text-gray-600 dark:text-gray-300">
            {currentNiche.recommendations.google.map((item, index) => (
              <li key={`google-${index}`} className="flex items-start">
                <span className="text-green-500 mr-2">•</span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <img src="https://static.xx.fbcdn.net/rsrc.php/y1/r/4lCu2zih0ca.svg" alt="Meta Ads" className="h-6 w-6 mr-2" />
            Meta Ads - Recomendações
          </h3>
          <ul className="space-y-3 text-gray-600 dark:text-gray-300">
            {currentNiche.recommendations.meta.map((item, index) => (
              <li key={`meta-${index}`} className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <PieChartIcon className="h-6 w-6 mr-2 text-purple-500" />
            Estratégias Gerais
          </h3>
          <ul className="space-y-3 text-gray-600 dark:text-gray-300">
            {currentNiche.recommendations.general.map((item, index) => (
              <li key={`general-${index}`} className="flex items-start">
                <span className="text-purple-500 mr-2">•</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Insights do Nicho - {selectedNiche}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Tendências 2025</h4>
            <ul className="space-y-2 text-gray-600 dark:text-gray-300">
              {selectedNiche === 'E-commerce' && (
                <>
                  <li>• Crescimento de 18% em compras por redes sociais</li>
                  <li>• Aumento de 25% em buscas por "entrega no mesmo dia"</li>
                  <li>• Vídeos curtos convertem 3x mais que imagens estáticas</li>
                </>
              )}
              {selectedNiche === 'Saúde e Bem-estar' && (
                <>
                  <li>• Buscas por "saúde mental" cresceram 40% em 2024</li>
                  <li>• Apps de meditação com alta retenção (65%)</li>
                  <li>• Conteúdo educativo gera 2x mais leads</li>
                </>
              )}
              {selectedNiche === 'Tecnologia e SaaS' && (
                <>
                  <li>• Demanda por soluções de IA cresce 35% ao ano</li>
                  <li>• Webinars têm taxa de conversão de 12% para trials</li>
                  <li>• B2B prioriza conteúdo técnico (whitepapers, cases)</li>
                </>
              )}
              {selectedNiche === 'Imobiliário' && (
                <>
                  <li>• 60% das buscas começam no mobile</li>
                  <li>• Tours virtuais aumentam contatos em 45%</li>
                  <li>• Dados de bairro são decisivos na conversão</li>
                </>
              )}
              {selectedNiche === 'Vendas/Varejo' && (
                <>
                  <li>• 70% dos consumidores pesquisam online antes de comprar</li>
                  <li>• Ofertas com estoque local convertem 40% mais</li>
                  <li>• Integração click-and-collect cresce 30% ao ano</li>
                </>
              )}
              {selectedNiche === 'Serviços Profissionais' && (
                <>
                  <li>• Buscas por "urgente" crescem 25% ao ano</li>
                  <li>• Disponibilidade imediata aumenta conversão em 50%</li>
                  <li>• Avaliações online influenciam 80% das contratações</li>
                </>
              )}
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Alertas de Mercado</h4>
            <ul className="space-y-2 text-gray-600 dark:text-gray-300">
              {selectedNiche === 'E-commerce' && (
                <>
                  <li>• CPCs podem subir 15% no Q4 (Black Friday)</li>
                  <li>• Meta Ads restringindo pixels de conversão</li>
                  <li>• Google priorizando anúncios com avaliações</li>
                </>
              )}
              {selectedNiche === 'Saúde e Bem-estar' && (
                <>
                  <li>• Regulamentações mais rígidas em claims</li>
                  <li>• Necessidade de certificados em anúncios</li>
                  <li>• Conteúdo médico requer aprovação manual</li>
                </>
              )}
              {selectedNiche === 'Tecnologia e SaaS' && (
                <>
                  <li>• Concorrência acirrada por termos B2B</li>
                  <li>• Custo por lead qualificado pode aumentar</li>
                  <li>• Necessidade de provas sociais (cases)</li>
                </>
              )}
              {selectedNiche === 'Imobiliário' && (
                <>
                  <li>• Sazonalidade forte no primeiro semestre</li>
                  <li>• Google exigindo mais transparência em preços</li>
                  <li>• Vídeos curtos performam melhor que fotos</li>
                </>
              )}
              {selectedNiche === 'Vendas/Varejo' && (
                <>
                  <li>• Concorrência local aumenta nos fins de semana</li>
                  <li>• Necessidade de atualizar estoque em tempo real</li>
                  <li>• Google My Business essencial para visibilidade</li>
                </>
              )}
              {selectedNiche === 'Serviços Profissionais' && (
                <>
                  <li>• Alta sazonalidade em períodos pós-feriados</li>
                  <li>• Concorrência por termos genéricos muito acirrada</li>
                  <li>• Google verificando licenças profissionais</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricsDisplay;